import {
  AwsGateCallbacks,
  AwsGateError,
  AwsGateRequest,
  ServiceType,
  Token,
} from "src/types";

import { useAwsGateError } from "./aws-gate-error.hook";
import { useAwsGateLoader } from "./aws-gate-loader.hook";
import { useAwsGateRequest } from "./aws-gate-request.hook";
import Cookies from "js-cookie";
import {
  GET_TOKEN_HEADER,
  useGetIdToken,
} from "../terminals/token/token.gates";
import { COGNITO_ID_TOKEN_KEY, ITAS_AR_TOKEN_KEY, ENV } from "src/config";

const retryGetToken = async (getIdToken: any) => {
  let result = false;

  await getIdToken(
    {
      headers: GET_TOKEN_HEADER,
    },
    {
      onSuccess: (response: Token) => {
        const idToken = response?.idToken;
        if (idToken) {
          Cookies.set(COGNITO_ID_TOKEN_KEY, `Bearer ${idToken}`);

          result = true;
        } else {
          result = false;
        }
      },
      onFailure: () => {
        result = false;
      },
    },
  );

  return result;
};

export const useAws = <T, D>(gateId: string, service: ServiceType) => {
  const { doRequest } = useAwsGateRequest<T, D>(gateId, service);
  const { gateError, saveGateError, removeGateError } = useAwsGateError(gateId);
  const { idle, pending, toIdle, toPending } = useAwsGateLoader(gateId);
  const { crossGate: getIdToken } = useGetIdToken();

  const crossGate = async (
    request: AwsGateRequest<T>,
    {
      onSuccess,
      onFailure = saveGateError,
      onIdle = toIdle,
      onPending = toPending,
    }: AwsGateCallbacks<D>,
  ) => {
    const requestWithCookie = {
      ...request,
      headers: {
        ...request.headers,
        Authorization: Cookies.get(COGNITO_ID_TOKEN_KEY),
        "x-auth-token": Cookies.get(ITAS_AR_TOKEN_KEY) || "",
        ...(ENV === "dev" && {
          "x-api-security-key": "he6K8nNzrw6Sz3oNK1JWpLnpkul0GBsc",
        }),
      },
    };

    try {
      if ((request as any).body instanceof FormData) {
        delete requestWithCookie.headers["Content-Type"];
      }
      onPending?.();
      onSuccess?.(await doRequest(requestWithCookie));
    } catch (err: unknown) {
      let castedError = err as AwsGateError;

      const isAuthError = castedError.response?.status == 401;

      if (isAuthError) {
        try {
          const result = await retryGetToken(getIdToken);

          if (result) {
            const retriedResponse = await doRequest({
              ...requestWithCookie,
              headers: {
                ...requestWithCookie.headers,
                Authorization: Cookies.get(COGNITO_ID_TOKEN_KEY),
              },
            });
            onSuccess?.(retriedResponse);
            return;
          }
        } catch (err_int) {
          castedError = err_int as AwsGateError;
        }
      }

      onFailure?.(castedError);
    } finally {
      onIdle?.();
    }
  };

  return { gateError, removeGateError, idle, pending, crossGate };
};
